import { useState } from 'react';
import type { FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { formatDateTime } from '../../lib/format';
import { t } from '../../lib/i18n';
import { ErrorLine, ErrorPanel, fieldErrors } from '../../components/ErrorPanel';
import { useToast } from '../../components/Toast';
import { Button, Card, EmptyRow, SelectField, Spinner, TableWrap, TextField, Th } from '../../components/ui';
import { BASE, SrStaff, StaffArray, keys } from './api';
import type { SrBuilding } from './api';

/** Building administrators and fire marshals. Platform admins add and remove them. */
export function StaffTab({ building, canManage }: { building: SrBuilding; canManage: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [form, setForm] = useState({ email: '', fullName: '', role: 'building_admin' });
  const q = useQuery({ queryKey: keys.staff(building.id), queryFn: () => api.get(`${BASE}/${building.id}/staff`, { schema: StaffArray }) });
  const refresh = () => qc.invalidateQueries({ queryKey: ['shared-reception', 'platform'] });
  const add = useMutation({
    mutationFn: () =>
      api.post(`${BASE}/${building.id}/staff`, {
        body: { email: form.email, role: form.role, ...(form.fullName.trim() ? { fullName: form.fullName.trim() } : {}) },
        schema: SrStaff,
      }),
    onSuccess: async () => {
      setForm({ email: '', fullName: '', role: 'building_admin' });
      await refresh();
      toast(t('srp.staff.added'));
    },
  });
  const remove = useMutation({
    mutationFn: (accountId: string) => api.delete(`${BASE}/${building.id}/staff/${accountId}`),
    onSuccess: async () => {
      await refresh();
      toast(t('srp.staff.removed'));
    },
  });
  const errs = fieldErrors(add.error);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    add.mutate();
  };
  return (
    <div className="space-y-6">
      {canManage && building.status === 'active' ? (
        <Card title={t('srp.staff.add')}>
          <form onSubmit={submit} className="grid gap-4 sm:grid-cols-4 sm:items-end" noValidate>
            <TextField label={t('srp.staff.email')} type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} error={errs['email']} required />
            <TextField label={t('srp.staff.fullName')} value={form.fullName} onChange={(e) => setForm((f) => ({ ...f, fullName: e.target.value }))} error={errs['fullName']} />
            <SelectField label={t('srp.staff.role')} value={form.role} onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}>
              <option value="building_admin">{t('sr.role.building_admin')}</option>
              <option value="building_marshal">{t('sr.role.building_marshal')}</option>
            </SelectField>
            <div>
              <Button type="submit" variant="primary" disabled={add.isPending || !form.email}>
                {t('srp.staff.addSubmit')}
              </Button>
            </div>
          </form>
          <ErrorLine error={Object.keys(errs).length ? null : add.error} />
        </Card>
      ) : null}
      <ErrorLine error={remove.error} />
      {q.isPending ? (
        <Spinner />
      ) : q.isError ? (
        <ErrorPanel error={q.error} onRetry={() => void q.refetch()} />
      ) : (
        <TableWrap caption={t('srp.staff.table')}>
          <thead className="border-b border-slate-200 bg-slate-50">
            <tr>
              <Th>{t('srp.staff.fullName')}</Th>
              <Th>{t('srp.staff.email')}</Th>
              <Th>{t('srp.staff.role')}</Th>
              <Th>{t('srp.staff.col.phones')}</Th>
              <Th>{t('srp.staff.col.added')}</Th>
              {canManage ? <Th>{t('srp.col.actions')}</Th> : null}
            </tr>
          </thead>
          <tbody>
            {q.data.length === 0 ? <EmptyRow cols={canManage ? 6 : 5} /> : null}
            {q.data.map((s) => (
              <tr key={s.accountId} className="border-t border-slate-100">
                <td className="px-4 py-3 font-medium">{s.fullName}</td>
                <td className="px-4 py-3">{s.email}</td>
                <td className="px-4 py-3">{t(`sr.role.${s.role}`)}</td>
                <td className="px-4 py-3">{s.phones}</td>
                <td className="px-4 py-3">{formatDateTime(s.addedAt, building.timeZone)}</td>
                {canManage ? (
                  <td className="px-4 py-3">
                    <Button onClick={() => remove.mutate(s.accountId)} disabled={remove.isPending} aria-label={t('srp.staff.removeNamed', { name: s.fullName })}>
                      {t('srp.staff.remove')}
                    </Button>
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </TableWrap>
      )}
    </div>
  );
}
